import { Activity, AlertTriangle, Cpu, Database, ExternalLink, Eye, HardDrive, Layers, MoreHorizontal, Play, Server, Square, Trash2, Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Paged } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Kpi } from '@/features/finance/components/FinanceBits'
import { toastError } from '@/features/finance/ui'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney, fmtRelative } from '../shared/format'
import { useDecommission, useInfra, useSetMaintenance, useTogglePlacement } from './api'
import { Toolbar, UsageBar } from './components/AdminBits'
import type { InfraPlacement, InfraServer, PlacementStatus, ServerRole, ServerStatus } from './types'
import { includesTr } from './ui'

const STATUS_TONE: Record<ServerStatus, BadgeTone> = { healthy: 'success', degraded: 'warning', maintenance: 'info', down: 'danger' }
const ROLE_TONE: Record<ServerRole, BadgeTone> = { production: 'primary', demo: 'info', staging: 'neutral', backup: 'outline' }
const PLACEMENT_TONE: Record<PlacementStatus, BadgeTone> = { running: 'success', stopped: 'warning', decommissioned: 'neutral' }
const LATEST: Record<string, string> = { orvapos: '4.12.0', klinikpanel: '2.7.3' }
const latestFor = (url: string) => Object.entries(LATEST).find(([k]) => url.includes(k))?.[1]

export function InfraPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const infra = useInfra()
  const maintenance = useSetMaintenance()
  const [tab, setTab] = useState<'servers' | 'placements'>('servers')
  const [q, setQ] = useState('')
  const [server, setServer] = useState('all')
  const [env, setEnv] = useState<InfraPlacement['env'] | 'all'>('all')
  const [project, setProject] = useState('all')
  const canWrite = can('infra.servers.write')
  const d = infra.data

  const servers = (d?.servers ?? []).filter((s) => includesTr(`${s.name} ${s.hostname} ${s.ip} ${s.location} ${s.provider}`, q))
  const placements = (d?.placements ?? []).filter(
    (p) =>
      (server === 'all' || (server === 'customer' ? p.serverId === null : p.serverId === server)) &&
      (env === 'all' || p.env === env) &&
      (project === 'all' || p.projectId === project) &&
      includesTr(`${p.url} ${p.customerName} ${p.version} ${p.projectName} ${p.hostLabel}`, q),
  )
  const live = d?.placements.filter((p) => p.status !== 'decommissioned') ?? []
  const alerts = d?.servers.filter((s) => s.status === 'degraded' || s.status === 'down' || s.disk >= 85) ?? []
  const avgCpu = d?.servers.length ? Math.round(d.servers.reduce((n, s) => n + s.cpu, 0) / d.servers.length) : 0

  const showPlacements = (serverId: string) => {
    setServer(serverId)
    setQ('')
    setTab('placements')
  }

  const toggleMaintenance = (s: InfraServer) => {
    const on = s.status !== 'maintenance'
    void confirmModal({
      title: on ? t('admin.infra.maintenanceOnTitle') : t('admin.infra.maintenanceOffTitle'),
      description: on ? t('admin.infra.maintenanceOnBody', { name: s.name, count: live.filter((p) => p.serverId === s.id).length }) : t('admin.infra.maintenanceOffBody', { name: s.name }),
      confirmLabel: on ? t('admin.infra.maintenanceOn') : t('admin.infra.maintenanceOff'),
    }).then((ok) => (ok ? maintenance.mutateAsync({ id: s.id, on }).then(() => toast.success(t('common.saved'))).catch(toastError) : undefined))
  }

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.admin')} title={t('admin.infra.title')} description={t('admin.infra.subtitle')} />

      {infra.isError ? (
        <ErrorState error={infra.error} onRetry={() => void infra.refetch()} />
      ) : !d ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Server} tone="primary" label={t('admin.infra.kpi.servers')} value={`${d.servers.filter((s) => s.status === 'healthy').length}/${d.servers.length}`} footer={t('admin.infra.kpi.serversHint')} />
            <Kpi icon={Layers} tone="success" label={t('admin.infra.kpi.placements')} value={live.length} footer={t('admin.infra.kpi.placementsHint', { count: live.filter((p) => p.env === 'production').length })} />
            <Kpi icon={Cpu} tone="info" label={t('admin.infra.kpi.cpu')} value={`%${avgCpu}`} footer={t('admin.infra.kpi.cpuHint')} />
            <Kpi icon={AlertTriangle} tone={alerts.length ? 'danger' : 'neutral'} label={t('admin.infra.kpi.alerts')} value={alerts.length} footer={alerts.length ? alerts.map((a) => a.name).join(', ') : t('admin.infra.kpi.alertsOk')} />
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList>
              <TabsTrigger value="servers">{t('admin.infra.servers')}</TabsTrigger>
              <TabsTrigger value="placements">{t('admin.infra.placements')}</TabsTrigger>
            </TabsList>
          </Tabs>

          <section className="overflow-hidden rounded-2xl border bg-card shadow-xs">
            <Toolbar q={q} onQ={setQ} placeholder={t('admin.infra.search')} count={t('admin.common.count', { count: tab === 'servers' ? servers.length : placements.length })}>
              {tab === 'placements' ? (
                <>
                  <Select
                    className="w-44"
                    value={server}
                    onChange={setServer}
                    aria-label={t('admin.infra.server')}
                    options={[{ value: 'all', label: t('admin.infra.allServers') }, ...d.servers.map((s) => ({ value: s.id, label: s.name })), { value: 'customer', label: t('admin.infra.customerServer') }]}
                  />
                  <Select
                    className="w-44"
                    value={project}
                    onChange={setProject}
                    aria-label={t('admin.infra.columns.project')}
                    options={[{ value: 'all', label: t('admin.infra.allProjects') }, ...[...new Map(d.placements.map((p) => [p.projectId, p.projectName])).entries()].map(([id, name]) => ({ value: id, label: name }))]}
                  />
                  <Select
                    className="w-36"
                    value={env}
                    onChange={setEnv}
                    aria-label={t('admin.infra.env')}
                    options={[{ value: 'all', label: t('admin.infra.allEnvs') }, ...(['production', 'demo', 'staging'] as const).map((e) => ({ value: e, label: t(`admin.infra.envs.${e}`) }))]}
                  />
                </>
              ) : null}
            </Toolbar>

            {tab === 'servers' ? (
              servers.length === 0 ? (
                <EmptyState icon={Server} title={t('admin.common.noMatch')} />
              ) : (
                <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-2 2xl:grid-cols-3">
                  {servers.map((s) => {
                    const count = live.filter((p) => p.serverId === s.id).length
                    return (
                      <article key={s.id} className={cn('flex flex-col gap-3 rounded-xl border p-4', s.status === 'degraded' && 'border-warning/50', s.status === 'down' && 'border-destructive/50')}>
                        <header className="flex items-start gap-3">
                          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl border bg-muted/50">
                            <Server className="size-5 text-muted-foreground" aria-hidden />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <h3 className="font-semibold">{s.name}</h3>
                              <Badge tone={STATUS_TONE[s.status]}>{t(`admin.infra.status.${s.status}`)}</Badge>
                              <Badge tone={ROLE_TONE[s.role]}>{t(`admin.infra.roles.${s.role}`)}</Badge>
                            </div>
                            <p className="truncate font-mono text-xs text-muted-foreground">
                              {s.hostname} · {s.ip}
                            </p>
                          </div>
                        </header>
                        <p className="text-xs text-muted-foreground">
                          {s.provider} · {s.location} · {s.os} · {s.vcpu} vCPU / {s.ramGb} GB / {s.diskGb >= 1000 ? `${s.diskGb / 1000} TB` : `${s.diskGb} GB`}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {[...new Map(live.filter((p) => p.serverId === s.id).map((p) => [p.projectId, p.projectName])).entries()].map(([pid, name]) => (
                            <Badge key={pid} tone="outline">
                              {name} · {live.filter((p) => p.serverId === s.id && p.projectId === pid).length}
                            </Badge>
                          ))}
                          {!live.some((p) => p.serverId === s.id) ? <span className="text-xs text-muted-foreground">{t('admin.infra.noProjects')}</span> : null}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <UsageBar value={s.cpu} label="CPU" />
                          <UsageBar value={s.ram} label="RAM" />
                          <UsageBar value={s.disk} label={t('admin.infra.disk')} />
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Activity className="size-3.5" aria-hidden />
                            {s.status === 'maintenance' ? t('admin.infra.inMaintenance') : t('admin.infra.uptime', { count: s.uptimeDays })}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <HardDrive className="size-3.5" aria-hidden />
                            {s.lastBackupAt ? t('admin.infra.backup', { when: fmtRelative(s.lastBackupAt) }) : t('admin.infra.noBackup')}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs">
                          <span className="text-muted-foreground">{t('admin.infra.detail.since', { date: fmtDate(s.startedAt) })}</span>
                          <span>
                            <span className="font-semibold tabular-nums">{fmtMoney(s.monthlyCost)}</span>
                            <span className="text-muted-foreground"> {t('admin.infra.perMonth')}</span>
                          </span>
                        </div>
                        <div className="mt-auto flex flex-wrap gap-2">
                          <Button size="sm" asChild>
                            <Link to={`/admin/infra/servers/${s.id}`}>
                              <Eye />
                              {t('admin.infra.detail.open')}
                            </Link>
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => showPlacements(s.id)}>
                            <Layers />
                            {t('admin.infra.placementCount', { count })}
                          </Button>
                          {canWrite && s.role !== 'backup' ? (
                            <Button size="sm" variant="ghost" onClick={() => toggleMaintenance(s)} disabled={maintenance.isPending}>
                              <Wrench />
                              {s.status === 'maintenance' ? t('admin.infra.maintenanceOff') : t('admin.infra.maintenanceOn')}
                            </Button>
                          ) : null}
                        </div>
                      </article>
                    )
                  })}
                </div>
              )
            ) : placements.length === 0 ? (
              <EmptyState icon={Layers} title={t('admin.common.noMatch')} />
            ) : (
              <Paged rows={placements} resetKey={[q, server, env, project]}>
                {(page) => <PlacementsTable rows={page} servers={d.servers} canWrite={canWrite} />}
              </Paged>
            )}
          </section>
        </div>
      )}
    </>
  )
}

function PlacementsTable({ rows, servers, canWrite }: { rows: InfraPlacement[]; servers: InfraServer[]; canWrite: boolean }) {
  const { t } = useTranslation()
  const toggle = useTogglePlacement()
  const decommission = useDecommission()
  const serverName = (id: string | null) => (id === null ? t('admin.infra.customerServer') : (servers.find((s) => s.id === id)?.name ?? id))

  const onDecommission = (p: InfraPlacement) =>
    void confirmModal({
      title: t('admin.infra.decommissionTitle'),
      description: t('admin.infra.decommissionBody', { url: p.url }),
      confirmLabel: t('admin.infra.decommission'),
      destructive: true,
    }).then((ok) => (ok ? decommission.mutateAsync(p.id).then(() => toast.success(t('admin.infra.decommissioned'))).catch(toastError) : undefined))

  return (
    <Table>
      <THead>
        <TR className="hover:bg-transparent">
          <TH>{t('admin.infra.columns.url')}</TH>
          <TH>{t('admin.infra.columns.customer')}</TH>
          <TH>{t('admin.infra.columns.project')}</TH>
          <TH>{t('admin.infra.server')}</TH>
          <TH>{t('admin.infra.columns.version')}</TH>
          <TH className="text-right">
            <Database className="inline size-3.5" aria-label={t('admin.infra.columns.db')} />
          </TH>
          <TH>{t('admin.common.status')}</TH>
          <TH className="w-12" />
        </TR>
      </THead>
      <TBody>
        {rows.map((p) => {
          const latest = latestFor(p.url)
          const behind = !!latest && p.status !== 'decommissioned' && p.version !== latest && !p.version.includes('-rc')
          const gone = p.status === 'decommissioned'
          return (
            <TR key={p.id} className={cn(gone && 'text-muted-foreground')}>
              <TD>
                {gone ? (
                  <span className="font-mono text-sm line-through">{p.url}</span>
                ) : !p.serverId ? (
                  <span className="font-mono text-sm">{p.url}</span>
                ) : (
                  <a href={`https://${p.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-sm hover:text-primary hover:underline">
                    {p.url}
                    <ExternalLink className="size-3" aria-hidden />
                  </a>
                )}
                <p className="text-xs text-muted-foreground">
                  {t(`admin.infra.envs.${p.env}`)} · {t('admin.infra.since', { date: fmtDate(p.createdAt) })}
                </p>
              </TD>
              <TD>{p.customerId ? <Link to={`/customers/${p.customerId}`} className="hover:text-primary hover:underline">{p.customerName}</Link> : p.customerName}</TD>
              <TD className="text-sm">{p.projectName}</TD>
              <TD className="text-xs">
                <span className={p.serverId ? 'font-mono' : undefined}>{serverName(p.serverId)}</span>
                {p.hostLabel ? <span className="block text-muted-foreground">{p.hostLabel}</span> : null}
              </TD>
              <TD>
                <span className="font-mono text-xs">{p.version}</span>
                {behind ? (
                  <Badge tone="warning" className="ml-1.5">
                    {t('admin.infra.updateAvailable', { version: latest })}
                  </Badge>
                ) : null}
              </TD>
              <TD className="text-right tabular-nums">{gone ? '—' : `${p.dbSizeGb.toLocaleString('tr-TR')} GB`}</TD>
              <TD>
                <Badge tone={PLACEMENT_TONE[p.status]}>{t(`admin.infra.placementStatus.${p.status}`)}</Badge>
              </TD>
              <TD>
                {canWrite && !gone ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label={t('common.rowActions', { name: p.url })}>
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-44">
                      <DropdownMenuItem onSelect={() => toggle.mutate(p.id, { onSuccess: () => toast.success(t('common.saved')), onError: toastError })}>
                        {p.status === 'running' ? <Square /> : <Play />}
                        {p.status === 'running' ? t('admin.infra.stop') : t('admin.infra.start')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onDecommission(p)} className="text-destructive [&_svg]:text-destructive">
                        <Trash2 />
                        {t('admin.infra.decommission')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </TD>
            </TR>
          )
        })}
      </TBody>
    </Table>
  )
}
